/**
 * Copyright Motili Inc., 2025 All Rights Reserved
 */

import { useContext, lazy } from 'react';
import { useLocation, Navigate } from 'react-router';

import { ServiceWorkerContext } from 'useServiceWorker';
import { UserTokenContext } from 'useUserToken';
import { useAppSelector } from './stores/redux/hooks';
import UpdateAppModal from './components/modal/UpdateAppModal';
import TimeoutModal from './components/modal/TimeoutModal';
import { MessagingRegister } from './components/messaging/MessagingRegister';
import DefaultErrorBoundary from './components/ErrorBoundary';
import { UIProvider } from './context/UIContext';
import { logout, isLoggedIn } from './services/AuthService';

const AdminRouter = lazy(() => import('../admin/AdminRouter'));
const ClientRouter = lazy(() => import('../client/ClientRouter'));
const ContractorRouter = lazy(() => import('../contractor/ContractorRouter'));

export default function AuthenticatedRouter(props) {
    const serviceWorker = useContext(ServiceWorkerContext);
    const userToken = useContext(UserTokenContext);

    const { isUpdateAvailable, updateAssets } = serviceWorker;
    const { showTimeoutDialog, expiredIn, extendToken } = userToken;
    let dialog;
    if (isUpdateAvailable) {
        dialog = <UpdateAppModal handleUpdate={updateAssets} />;
    } else if (showTimeoutDialog) {
        dialog = (
            <TimeoutModal
                handleExtend={extendToken}
                handleClose={() => {}}
                expiredIn={expiredIn}
            />
        );
    }
    return (
        <>
            {dialog}
            <AuthenticatedRouterComponent {...props} />
        </>
    );
}

const AuthenticatedRouterComponent = () => {
    const location = useLocation();

    const {
        authenticated,
        isMotiliAccount,
        isClientAccount,
        isContractorAccount,
    } = useAppSelector(state => state.authentication);

    // authenticated is rehydrated from redux-persist, so it can outlive the
    // session token. check the token itself before rendering a portal
    if (authenticated && isLoggedIn()) {
        if (isMotiliAccount) {
            return (
                <UIProvider initialLegacyUI>
                    <MessagingRegister />
                    <DefaultErrorBoundary>
                        <AdminRouter redirect={location?.search} />
                    </DefaultErrorBoundary>
                </UIProvider>
            );
        }

        if (isClientAccount) {
            return (
                <>
                    <MessagingRegister />
                    <DefaultErrorBoundary>
                        <ClientRouter redirect={location?.search} />
                    </DefaultErrorBoundary>
                </>
            );
        }

        if (isContractorAccount) {
            return (
                <>
                    <MessagingRegister />
                    <DefaultErrorBoundary>
                        <ContractorRouter redirect={location?.search} />
                    </DefaultErrorBoundary>
                </>
            );
        }
        // Log out user due to invalid auth
        logout();
    }
    return (
        <Navigate
            to={{
                pathname: '/login',
                search: `redirectTo=${location?.pathname}`,
            }}
        />
    );
};
